import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ratingOptions = ['Beginner', 'Tenured', 'Advanced', 'N/A'];

const initialFeedback = {
  batting: { score: '', comments: '' },
  bowling: { score: '', comments: '' },
  mindset: { score: '', comments: '' },
  fitness: { score: '', comments: '' },
};

const initialCategories = {
  batting: {
    straightBatShots: '',
    shotSelection: '',
    playingSpin: '',
    playingFast: '',
    power: '',
    footMovement: '',
  },
  bowling: {
    bowlingAction: '',
    accuracy: '',
    paceVariation: '',
    swing: '',
    bowlingVariation: '',
  },
  mindset: {
    gameSense: '',
    maintainsCalm: '',
    executeStrategies: '',
    teamPlayer: '',
  },
  fitness: {
    stamina: '',
    core: '',
    power: '',
    endurance: '',
  },
};

const CoachEvaluationForm = () => {
  const [players, setPlayers] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [coachProfile, setCoachProfile] = useState({ firstName: '', lastName: '', _id: '' });
  const [feedback, setFeedback] = useState(initialFeedback);
  const [categories, setCategories] = useState(initialCategories);
  const [coachComments, setCoachComments] = useState('');
  const [manualStats, setManualStats] = useState({
    gamesPlayed: '',
    totalRuns: '',
    totalWickets: '',
  });
  const [derivedStats, setDerivedStats] = useState({
    target: null,
    gapPercent: null,
    gameTime: '',
  });

  const [evaluationId, setEvaluationId] = useState(null);
  const [status, setStatus] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios
      .get('https://cricket-academy-backend.onrender.com/api/coach/players?status=Active', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setPlayers(res.data))
      .catch((err) => console.error('Player fetch error:', err));
  }, []);

  useEffect(() => {
    axios
      .get('https://cricket-academy-backend.onrender.com/api/coach/profile', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setCoachProfile(res.data))
      .catch((err) => console.error('Coach profile fetch error:', err.response?.data || err.message));
  }, []);

  useEffect(() => {
    if (!selectedPlayerId) return;

    axios
      .get(`https://cricket-academy-backend.onrender.com/api/player/${selectedPlayerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setSelectedPlayer(res.data))
      .catch((err) => console.error('Player detail fetch error:', err));

    axios
      .get(`https://cricket-academy-backend.onrender.com/api/evaluations/player/${selectedPlayerId}/latest`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const ev = res.data;
        if (ev && ev.status === 'Draft') {
          setEvaluationId(ev._id);
          setStatus(ev.status);
          setFeedback(ev.feedback || initialFeedback);
          setCategories(ev.categories || initialCategories);
          setCoachComments(ev.coachComments || '');
          setManualStats({
            gamesPlayed: ev.gamesPlayed || '',
            totalRuns: ev.totalRuns || '',
            totalWickets: ev.totalWickets || '',
          });
          setDerivedStats({
            target: ev.targetGames || null,
            gapPercent: ev.gapPercent || null,
            gameTime: '',
          });
        } else {
          setEvaluationId(null);
          setStatus(null);
          setFeedback(initialFeedback);
          setCategories(initialCategories);
          setCoachComments('');
          setManualStats({ gamesPlayed: '', totalRuns: '', totalWickets: '' });
          setDerivedStats({ target: null, gapPercent: null, gameTime: '' });
        }
      })
      .catch((err) => console.error('Latest evaluation fetch error:', err));
  }, [selectedPlayerId]);

  const derivedCategory = (() => {
    const age = selectedPlayer?.age;
    const startYear = selectedPlayer?.competitiveStartYear;
    const currentYear = new Date().getFullYear();

    if (startYear && startYear > currentYear) {
      return 'Beginner';
    }
    if (age === undefined || age === null) return 'N/A';
    if (age < 11) return 'U11';
    if (age < 13) return 'U13';
    if (age < 15) return 'U15';
    if (age < 17) return 'U17';
    return 'Adult';
  })();

  const handleSaveStats = () => {
    const categoryTargets = {
      Beginner: 0,
      U11: 15,
      U13: 30,
      U15: 45,
      U17: 60,
      Adult: 60,
    };

    if (derivedCategory === 'Beginner') {
      setDerivedStats({ target: 0, gapPercent: 0, gameTime: 'Not Applicable' });
      return;
    }

    const targetPerYear = categoryTargets[derivedCategory] || 0;
    const yearsCompetitive = selectedPlayer?.competitiveStartYear
      ? Math.max(1, new Date().getFullYear() - selectedPlayer.competitiveStartYear + 1)
      : 1;

    const target = targetPerYear * yearsCompetitive;
    const played = parseInt(manualStats.gamesPlayed) || 0;
    const gapPercent = target > 0 ? Math.round(((target - played) / target) * 100) : 0;
    const gameTime =
      gapPercent >= 80 ? 'Major Gap' :
      gapPercent >= 50 ? 'Need Some More' :
      'On Track';

    setDerivedStats({ target, gapPercent, gameTime });
  };

  const handleSubmit = async (statusOverride = 'Submitted') => {
    if (!coachProfile._id) {
      alert('Coach profile not loaded yet. Please wait a moment and try again.');
      return;
    }

    try {
      const payload = {
        player: selectedPlayerId,
        coach: coachProfile._id,
        feedback,
        categories,
        coachComments,
        gamesPlayed: parseInt(manualStats.gamesPlayed) || 0,
        totalRuns: parseInt(manualStats.totalRuns) || 0,
        totalWickets: parseInt(manualStats.totalWickets) || 0,
        status: statusOverride,
        ageCategory: derivedCategory,
        targetGames: derivedStats.target,
        gapPercent: derivedStats.gapPercent,
        gameTime: derivedStats.gameTime,
      };

      if (evaluationId && status === 'Draft') {
        if (statusOverride === 'Draft') {
          await axios.put(
            `https://cricket-academy-backend.onrender.com/api/evaluations/${evaluationId}`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          alert('Draft updated!');
        } else {
          await axios.put(
            `https://cricket-academy-backend.onrender.com/api/evaluations/${evaluationId}/submit`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          alert('Draft submitted!');
        }
      } else {
        await axios.post(
          'https://cricket-academy-backend.onrender.com/api/evaluations',
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert(statusOverride === 'Draft' ? 'Draft saved!' : 'Evaluation submitted!');
      }

      navigate('/coach/dashboard');
    } catch (err) {
      console.error('Evaluation submission error:', err.response?.data || err.message || err.toString());
      alert('Failed to save evaluation.');
    }
  };
  return (
    <div className="max-w-5xl mx-auto p-6 bg-gray-50 rounded-lg shadow">
      <h2 className="text-2xl font-bold text-blue-700 mb-6">Coach Evaluation Form</h2>

      <label className="block font-medium text-gray-700 mt-4">
        Select Player
        <select
          value={selectedPlayerId}
          onChange={(e) => setSelectedPlayerId(e.target.value)}
          className="border px-3 py-2 rounded w-full mt-1"
          required
        >
          <option value="">Choose a player</option>
          {players.map((p) => (
            <option key={p._id} value={p._id}>
              {p.firstName} {p.lastName}
            </option>
          ))}
        </select>
      </label>

      {selectedPlayer && (
        <div className="bg-gray-100 border-l-4 border-green-400 p-4 rounded shadow text-sm text-gray-700 space-y-1">
          <div><strong>Player Name:</strong> {selectedPlayer.firstName} {selectedPlayer.lastName}</div>
          <div><strong>Category:</strong> {derivedCategory}</div>
          <div><strong>CricClubs ID:</strong> {selectedPlayer.cricclubsID}</div>
          <div><strong>Player Profile:</strong> {selectedPlayer.role}</div>
          <div>
            <strong>Competitive Years:</strong>{' '}
            {selectedPlayer.competitiveStartYear
              ? new Date().getFullYear() - selectedPlayer.competitiveStartYear
              : 'N/A'}
          </div>
          <div><strong>Age:</strong> {selectedPlayer.age}</div>
          <div>
            <a
              href={`https://cricclubs.com/PremierCricAcad/viewPlayer.do?playerId=${selectedPlayer.cricclubsID}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              View CricClubs Profile ↗
            </a>
            <p className="text-sm text-gray-500 mt-1">
              Please open the link, review the stats, and enter them manually below.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Games Played</label>
              <input
                type="number"
                value={manualStats.gamesPlayed}
                onChange={(e) =>
                  setManualStats({ ...manualStats, gamesPlayed: e.target.value })
                }
                className="border px-3 py-2 rounded w-full"
                placeholder="Enter manually"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Runs</label>
              <input
                type="number"
                value={manualStats.totalRuns}
                onChange={(e) =>
                  setManualStats({ ...manualStats, totalRuns: e.target.value })
                }
                className="border px-3 py-2 rounded w-full"
                placeholder="Enter manually"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Wickets</label>
              <input
                type="number"
                value={manualStats.totalWickets}
                onChange={(e) =>
                  setManualStats({ ...manualStats, totalWickets: e.target.value })
                }
                className="border px-3 py-2 rounded w-full"
                placeholder="Enter manually"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={handleSaveStats}
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Save Stats
          </button>

          {derivedStats.target !== null && (
            <div className="mt-4 space-y-1 text-sm text-gray-700">
              <div><strong>Target:</strong> {derivedStats.target}</div>
              <div><strong>Gap:</strong> {derivedStats.gapPercent}%</div>
              <div><strong>Game Time:</strong> {derivedStats.gameTime}</div>
            </div>
          )}
        </div>
      )}
      <form className="space-y-8 mt-6">
        {['batting', 'bowling', 'mindset', 'fitness'].map((group) => (
          <div key={group} className="bg-white p-6 rounded-lg shadow space-y-4">
            <h3 className="text-xl font-semibold text-blue-700 capitalize">{group} Evaluation</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-medium text-gray-700">
                  {group.charAt(0).toUpperCase() + group.slice(1)} Score (1–10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={feedback[group].score}
                  onChange={(e) =>
                    setFeedback({
                      ...feedback,
                      [group]: {
                        ...feedback[group],
                        score: parseInt(e.target.value) || '',
                      },
                    })
                  }
                  className="border px-3 py-2 rounded w-full"
                  required
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700">Comments</label>
                <textarea
                  value={feedback[group].comments}
                  onChange={(e) =>
                    setFeedback({
                      ...feedback,
                      [group]: {
                        ...feedback[group],
                        comments: e.target.value,
                      },
                    })
                  }
                  className="border px-3 py-2 rounded w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(categories[group]).map(([field, value]) => (
                <label key={field} className="block">
                  <span className="block font-medium text-gray-700 mb-1">
                    {field.replace(/([A-Z])/g, ' $1')}
                  </span>
                  <select
                    value={value}
                    onChange={(e) =>
                      setCategories({
                        ...categories,
                        [group]: {
                          ...categories[group],
                          [field]: e.target.value,
                        },
                      })
                    }
                    className="border px-3 py-2 rounded w-full"
                    required
                  >
                    <option value="">Select</option>
                    {ratingOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </div>
        ))}
        <div className="bg-white p-6 rounded-lg shadow space-y-2">
          <h3 className="text-xl font-semibold text-gray-700">Coach Comments</h3>
          <textarea
            value={coachComments}
            onChange={(e) => setCoachComments(e.target.value)}
            className="border px-3 py-2 rounded w-full"
            rows={4}
            placeholder="Add any final observations or recommendations..."
          />
        </div>

        <div className="text-right space-x-3">
          <button
            type="button"
            disabled={!coachProfile._id || !selectedPlayerId}
            onClick={() => handleSubmit('Draft')}
            className={`px-6 py-2 rounded transition ${
              !coachProfile._id || !selectedPlayerId
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-yellow-500 text-white hover:bg-yellow-600'
            }`}
          >
            {evaluationId && status === 'Draft' ? 'Update Draft' : 'Save Draft'}
          </button>

          <button
            type="button"
            disabled={!coachProfile._id || !selectedPlayerId}
            onClick={() => handleSubmit('Submitted')}
            className={`px-6 py-2 rounded transition ${
              !coachProfile._id || !selectedPlayerId
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {evaluationId && status === 'Draft' ? 'Submit Draft' : 'Submit Evaluation'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CoachEvaluationForm;
